import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingBag, User } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCart } from "../context/CartContext";
import { useAuth } from "../contexts/AuthContext";
import LanguageSwitch from "./LanguageSwitch";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { count, setOpen: setCartOpen } = useCart();
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();

  // Nav items — re-built each render so labels follow the active language
  const NAV_LEFT = [
    { path: "/archives", labelKey: "nav.archives" },
    // { path: "/journal", labelKey: "nav.journal" }, // masqué temporairement avant lancement
  ];
  const NAV_RIGHT = [
    { path: "/vestiaire", labelKey: "nav.vestiaire" },
    { path: "/a-propos", labelKey: "nav.apropos" },
    // Contact intégré en fin de page À propos (/a-propos#contact)
  ];
  const NAV_ALL = [...NAV_LEFT, ...NAV_RIGHT];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#FBF8EF]/92 backdrop-blur-md border-b border-[#E5DFD3]"
          : "bg-[#FBF8EF]/70 backdrop-blur-sm"
      }`}
    >
      <div className="mx-auto max-w-[1800px] px-5 md:px-10 lg:px-14 grid grid-cols-3 items-center h-16 md:h-20">
        {/* LEFT */}
        <nav className="hidden lg:flex items-center gap-8 justify-self-start" data-testid="desktop-nav-left">
          {NAV_LEFT.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-link-${item.path.slice(1)}`}
              className={`nav-link font-sans text-[11px] tracking-[0.28em] uppercase text-[#121212] ${
                location.pathname.startsWith(item.path) ? "text-[#5C4033]" : ""
              }`}
            >
              {t(item.labelKey)}
            </Link>
          ))}
          {/* Language switch — right next to Archives */}
          <LanguageSwitch testId="desktop-lang-switch" />
        </nav>
        {/* mobile burger left */}
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="mobile-menu-open-btn"
          aria-label={t("nav.openMenu")}
          className="lg:hidden text-[#121212] p-2 -ml-2 justify-self-start"
        >
          <Menu size={20} strokeWidth={1.2} />
        </button>

        {/* CENTER LOGO */}
        <Link
          to="/"
          data-testid="logo-home-link"
          className="justify-self-center inline-flex"
          aria-label="Pulaar — Accueil"
        >
          <img
            src="/logo-pulaar.png"
            alt="Pulaar"
            className="h-10 md:h-12 w-auto select-none"
            draggable="false"
          />
        </Link>

        {/* RIGHT */}
        <div className="flex items-center gap-6 md:gap-8 justify-self-end">
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav-right">
            {NAV_RIGHT.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-link-${item.path.slice(1)}`}
                className={`nav-link font-sans text-[11px] tracking-[0.28em] uppercase text-[#121212] ${
                  location.pathname.startsWith(item.path) ? "text-[#5C4033]" : ""
                }`}
              >
                {t(item.labelKey)}
              </Link>
            ))}
          </nav>
          <button
            type="button"
            onClick={() => setCartOpen(true)}
            data-testid="cart-open-btn"
            aria-label={t("nav.openCart")}
            className="relative inline-flex items-center text-[#121212] p-1"
          >
            <ShoppingBag size={18} strokeWidth={1.2} />
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-1 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-[#121212] text-[#FBF8EF] text-[9px] tracking-wider font-medium flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </button>
          {/* Compte — masqué temporairement avant lancement
          <Link
            to={isAuthenticated ? "/mon-carnet" : "/connexion"}
            data-testid="header-account-link"
            aria-label={isAuthenticated ? (user?.name || "Mon carnet") : "Se connecter"}
            className="hidden md:inline-flex items-center text-[#121212] p-1"
          >
            {isAuthenticated && user?.picture ? (
              <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover border border-[#E5DFD3]" />
            ) : (
              <User size={18} strokeWidth={1.2} />
            )}
          </Link>
          */}
        </div>
      </div>

      {/* MOBILE MENU */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ y: -8 }}
            animate={{ y: 0 }}
            exit={{ y: -8, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, height: "100vh" }}
            className="z-[60] bg-[#FBF8EF] overflow-y-auto"
            data-testid="mobile-menu-panel"
          >
            <div className="flex items-center justify-between h-16 px-5 border-b border-[#E5DFD3] bg-[#FBF8EF]">
              <button
                onClick={() => setOpen(false)}
                data-testid="mobile-menu-close-btn"
                aria-label={t("nav.closeMenu")}
                className="text-[#121212] p-2 -ml-2"
              >
                <X size={22} strokeWidth={1.2} />
              </button>
              <Link to="/" onClick={() => setOpen(false)} className="inline-flex">
                <img
                  src="/logo-pulaar.png"
                  alt="Pulaar"
                  className="h-9 w-auto"
                />
              </Link>
              <button
                onClick={() => {
                  setOpen(false);
                  setCartOpen(true);
                }}
                aria-label={t("nav.cart")}
                className="relative text-[#121212] p-2 -mr-2"
              >
                <ShoppingBag size={20} strokeWidth={1.2} />
                {count > 0 && (
                  <span className="absolute top-0 right-0 min-w-[16px] h-4 px-1 rounded-full bg-[#121212] text-[#FBF8EF] text-[9px] flex items-center justify-center">
                    {count}
                  </span>
                )}
              </button>
            </div>

            <motion.nav
              initial="hidden"
              animate="visible"
              variants={{
                hidden: {},
                visible: { transition: { staggerChildren: 0.06, delayChildren: 0.08 } },
              }}
              className="px-8 pt-10 pb-12"
            >
              <motion.p
                variants={{
                  hidden: { opacity: 0, y: 8 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
                }}
                className="text-[10px] tracking-[0.32em] uppercase text-[#5C4033] mb-6"
              >
                — {t("nav.menu")}
              </motion.p>

              {/* Primary links — sober, separated by thin lines */}
              <ul className="border-t border-[#D8D2C5]">
                {NAV_ALL.map((item) => {
                  const active =
                    item.path === "/"
                      ? location.pathname === "/"
                      : location.pathname.startsWith(item.path);
                  return (
                    <motion.li
                      key={item.path}
                      variants={{
                        hidden: { opacity: 0, y: 8 },
                        visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
                      }}
                      className="border-b border-[#D8D2C5]"
                    >
                      <Link
                        to={item.path}
                        onClick={() => setOpen(false)}
                        data-testid={`mobile-nav-link-${item.path.slice(1)}`}
                        className="flex items-center justify-between py-4 group"
                      >
                        <span
                          className={`text-[12px] tracking-[0.32em] uppercase transition-colors duration-300 ${
                            active ? "text-[#5C4033]" : "text-[#121212]"
                          }`}
                        >
                          {t(item.labelKey)}
                        </span>
                        <span className="text-[11px] text-[#4A4641] transition-transform duration-500 group-active:translate-x-1">
                          →
                        </span>
                      </Link>
                    </motion.li>
                  );
                })}
              </ul>

              {/* Secondary — discreet contact links */}
              <motion.div
                variants={{
                  hidden: { opacity: 0 },
                  visible: { opacity: 1, transition: { duration: 0.5, delay: 0.1 } },
                }}
                className="mt-10 flex flex-col gap-3"
              >
                <a
                  href="https://instagram.com/pulaar.co"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="mobile-nav-instagram"
                  className="text-[11px] tracking-[0.28em] uppercase text-[#121212]"
                >
                  Instagram <span className="text-[#5C4033]">@pulaar.co</span>
                </a>
                <a
                  href="mailto:info@pulaar.co"
                  data-testid="mobile-nav-email"
                  className="text-[11px] tracking-[0.28em] uppercase text-[#121212]"
                >
                  info@pulaar.co
                </a>
                <p className="mt-6 text-[10px] tracking-[0.32em] uppercase text-[#4A4641]">
                  Dakar · Paris
                </p>
              </motion.div>

              {/* Language switch — bottom of mobile menu */}
              <motion.div
                variants={{
                  hidden: { opacity: 0 },
                  visible: { opacity: 1, transition: { duration: 0.5, delay: 0.18 } },
                }}
                className="mt-12 pt-6 border-t border-[#D8D2C5]"
              >
                <LanguageSwitch testId="mobile-lang-switch" />
              </motion.div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
